import type {
	RecordSource,
	RecordStepDirection,
	RecordStepperConfig,
	RecordStepperPromptChoice,
	RecordStepperStore,
	RecordStepperUnsaved,
	RecordStepperView
} from "~/components/recordStepper/recordStepperTypes";

import logger from "~/helpers/logger";
import recordSequence from "~/helpers/recordSequence";

const MAX_BLOCK_LOADS_PER_STEP = 5;

export const HIDDEN_RECORD_STEPPER_VIEW: RecordStepperView = Object.freeze({
	canNext: false,
	canPrevious: false,
	isStepping: false,
	isVisible: false,
	position: null,
	prompt: null,
	total: null
});

interface PendingTarget {
	id: string;
	index: number;
}

interface OpenPrompt {
	direction: RecordStepDirection;
	isSaving: boolean;
}

const isSameView = (a: RecordStepperView, b: RecordStepperView): boolean =>
	a.canNext === b.canNext &&
	a.canPrevious === b.canPrevious &&
	a.isStepping === b.isStepping &&
	a.isVisible === b.isVisible &&
	a.position === b.position &&
	a.total === b.total &&
	a.prompt?.canSave === b.prompt?.canSave &&
	a.prompt?.isSaving === b.prompt?.isSaving &&
	(a.prompt === null) === (b.prompt === null);

const saveInOrder = async (dirty: readonly RecordStepperUnsaved[]): Promise<boolean> => {
	for (const part of dirty) {
		if (!(await part.save())) return false;
	}
	return true;
};

const createRecordStepperStore = (): RecordStepperStore => {
	const listeners = new Set<() => void>();
	const unsavedReaders = new Set<() => RecordStepperUnsaved>();
	let config: null | RecordStepperConfig = null;
	let currentId: null | string = null;
	let anchorIndex: null | number = null;
	let isInList = false;
	let pendingTarget: null | PendingTarget = null;
	let isStepping = false;
	let stepToken = 0;
	let prompt: null | OpenPrompt = null;
	let view = HIDDEN_RECORD_STEPPER_VIEW;
	let connections = 0;
	let connectedSource: null | RecordSource = null;
	let disconnectSource: (() => void) | null = null;

	const dirtyParts = (): RecordStepperUnsaved[] => [...unsavedReaders].map((read) => read()).filter((u) => u.isDirty);

	const syncWithList = () => {
		if (!config || currentId === null) return;
		const index = config.source.getSnapshot().ids.indexOf(currentId);
		if (index < 0) return;
		isInList = true;
		anchorIndex = index;
	};

	const computeView = (): RecordStepperView => {
		if (!config || currentId === null || !isInList) return HIDDEN_RECORD_STEPPER_VIEW;
		const snapshot = config.source.getSnapshot();
		const place = recordSequence.placeAt(snapshot.ids, currentId, anchorIndex);
		if (!place) return HIDDEN_RECORD_STEPPER_VIEW;
		const canMove = !isStepping && prompt === null && !config.source.isBusy() && (config.canStep?.() ?? true);
		return {
			canNext: canMove && place.nextIndex !== null,
			canPrevious: canMove && place.previousIndex !== null,
			isStepping,
			isVisible: true,
			position: place.position,
			prompt: prompt && {
				canSave: dirtyParts().every((part) => part.canSave),
				isSaving: prompt.isSaving
			},
			total: snapshot.total
		};
	};

	const publish = () => {
		const next = computeView();
		if (isSameView(next, view)) return;
		view = next;
		for (const listener of listeners) listener();
	};

	const handleSourceChange = () => {
		syncWithList();
		publish();
	};

	const connectSource = () => {
		if (connections === 0 || !config || connectedSource === config.source) return;
		disconnectSource?.();
		connectedSource = config.source;
		disconnectSource = config.source.subscribe(handleSourceChange);
	};

	const cancelStep = () => {
		stepToken++;
		isStepping = false;
		pendingTarget = null;
	};

	const go = async (direction: RecordStepDirection) => {
		const token = ++stepToken;
		isStepping = true;
		publish();
		try {
			for (let loads = 0; loads <= MAX_BLOCK_LOADS_PER_STEP; loads++) {
				if (token !== stepToken || !config || currentId === null) return;
				const snapshot = config.source.getSnapshot();
				const place = recordSequence.placeAt(snapshot.ids, currentId, anchorIndex);
				const targetIndex = direction === "next" ? place?.nextIndex : place?.previousIndex;
				if (targetIndex == null) return;
				const targetId = snapshot.ids[targetIndex];
				if (targetId !== null) {
					pendingTarget = { id: targetId, index: targetIndex };
					await config.onStep(targetId);
					return;
				}
				if (loads === MAX_BLOCK_LOADS_PER_STEP || !(await config.source.load(targetIndex))) return;
			}
		} catch (error) {
			logger.error("Record stepper could not open the next record", error);
		} finally {
			if (token === stepToken) {
				isStepping = false;
				syncWithList();
				publish();
			}
		}
	};

	const configure = (next: RecordStepperConfig) => {
		const nextId = next.currentId ?? null;
		config = next;
		if (nextId !== currentId) {
			const arrivedByStep = pendingTarget?.id === nextId;
			currentId = nextId;
			if (arrivedByStep && pendingTarget) {
				anchorIndex = pendingTarget.index;
				isInList = true;
			} else {
				cancelStep();
				anchorIndex = null;
				isInList = false;
				prompt = null;
			}
		}
		connectSource();
		syncWithList();
		publish();
	};

	const connect = () => {
		connections++;
		connectSource();
		syncWithList();
		publish();
		return () => {
			connections--;
			if (connections > 0) return;
			disconnectSource?.();
			disconnectSource = null;
			connectedSource = null;
			cancelStep();
		};
	};

	const step = (direction: RecordStepDirection) => {
		publish();
		if (direction === "next" ? !view.canNext : !view.canPrevious) return;
		if (dirtyParts().length > 0) {
			prompt = { direction, isSaving: false };
			publish();
			return;
		}
		void go(direction);
	};

	const saveThenGo = async (direction: RecordStepDirection) => {
		const token = stepToken;
		prompt = { direction, isSaving: true };
		publish();
		let saved = false;
		try {
			saved = await saveInOrder(dirtyParts());
		} catch (error) {
			logger.error("Record stepper save failed", error);
		}
		if (token !== stepToken || prompt === null) return;
		prompt = null;
		publish();
		if (saved) void go(direction);
	};

	const resolvePrompt = (choice: RecordStepperPromptChoice) => {
		if (!prompt || prompt.isSaving) return;
		const { direction } = prompt;
		if (choice === "save") {
			if (view.prompt?.canSave) void saveThenGo(direction);
			return;
		}
		if (choice === "discard") for (const part of dirtyParts()) part.discard();
		prompt = null;
		publish();
		if (choice === "discard") void go(direction);
	};

	const registerUnsaved = (read: () => RecordStepperUnsaved) => {
		unsavedReaders.add(read);
		publish();
		return () => {
			unsavedReaders.delete(read);
			publish();
		};
	};

	const subscribe = (onChange: () => void) => {
		listeners.add(onChange);
		return () => {
			listeners.delete(onChange);
		};
	};

	return {
		configure,
		connect,
		getView: () => view,
		registerUnsaved,
		resolvePrompt,
		step,
		subscribe,
		unsavedChanged: publish
	};
};

export default createRecordStepperStore;
